/**
 * From traced stations to the alignment that is saved: what the review panel
 * shows (cant and gauge along the chainage) and what it edits (trim, bridged
 * stretches, smoothing, direction).
 *
 * Cant follows the clearance worker's convention (v1 rail-pair tool,
 * `scripts/clearance_geometry.py`): z right rail − z left rail, normalised to
 * the 1.5 m rail-head base the worker tilts by (alpha = atan2(cant, 1.5)), so
 * the stored value gives the tilt the scan shows whatever the head spacing.
 * Positive = right rail higher, looking along the alignment.
 */
import { RAIL_HEAD_WIDTH_M, STANDARD_GAUGE_M } from './detect';
import type { RailStation, StationStatus } from './trace';

/** The clearance worker's rail-head base (`RAIL_HEAD_SPACING_M`). */
export const CANT_BASE_M = 1.5;
/** Largest |cant| the clearance API accepts (`MAX_ABS_CANT_M`). */
export const MAX_ABS_CANT_M = 0.25;
/** Vertex spacing of the saved alignment (m). Chord error on R 300 m: 1.7 mm. */
export const VERTEX_SPACING_M = 2;
/** Gauge within this of its nominal is "ok", up to twice it "check" (m). */
export const GAUGE_OK_M = 0.01;
/** Nominal gauges a track is checked against (m): metre, Cape, standard, broad (Russian, Finnish, Iberian). */
export const NOMINAL_GAUGES_M = [1.0, 1.067, 1.435, 1.52, 1.524, 1.668] as const;
/** The nominal gauge nearest a measured one. */
export const nominalGauge = (gaugeM: number): number =>
  NOMINAL_GAUGES_M.reduce((best, g) => (Math.abs(g - gaugeM) < Math.abs(best - gaugeM) ? g : best), STANDARD_GAUGE_M);

/**
 * The track gauge set in the rail tool's settings: 'auto' grades against the nominal gauge
 * nearest the median measured one (`nominalGauge`); a choice grades against it, and the click's
 * direction finder prefers a rail pair that far apart (`TraceOptions.gaugeM`).
 */
export type TrackGaugeChoice = 'auto' | '1435' | '1520' | '1000' | 'custom';
export interface TrackGaugeSetting {
  choice: TrackGaugeChoice;
  /** The 'custom' gauge (mm), kept while another choice is made. */
  customMm: number;
}
export const DEFAULT_TRACK_GAUGE: TrackGaugeSetting = { choice: 'auto', customMm: 1435 };
/** A custom gauge must lie in here (mm): the follower takes rail pairs 0.8-1.75 m apart (head centres). */
export const CUSTOM_GAUGE_MIN_MM = 760;
export const CUSTOM_GAUGE_MAX_MM = 1676;

/** The gauge a setting asks for (m), or null for 'auto' (or a custom value out of range). */
export function trackGaugeM(g: TrackGaugeSetting): number | null {
  if (g.choice === 'auto') return null;
  const mm = g.choice === 'custom' ? g.customMm : Number(g.choice);
  return Number.isFinite(mm) && mm >= CUSTOM_GAUGE_MIN_MM && mm <= CUSTOM_GAUGE_MAX_MM ? mm / 1000 : null;
}

export const SMOOTHING_OPTIONS_M = [0, 3, 5, 10, 20] as const;
export const DEFAULT_SMOOTHING_M = 5;
export const MIN_ALIGNMENT_LENGTH_M = 4;
/** Positions are never smoothed over more than this (m); see `reviewTrace`. */
export const MAX_POSITION_SMOOTHING_M = 5;

/** 'rejected': measured, but an outlier against its neighbours (see `rejectOutliers`); treated as not measured. */
export type ReviewStatus = StationStatus | 'bridged' | 'rejected';

export type SuspectReason = 'outliers' | 'oneRail' | 'gauge';

/** A stretch the user should look at: repeated outliers, a long run on one rail, or a gauge far off nominal. */
export interface SuspectStretch {
  from: number;
  to: number;
  reason: SuspectReason;
  /** A gauge stretch: its largest departure from nominal, signed (m), from the raw readings. */
  devM?: number;
}

/** Outlier thresholds against the station's neighbours within ±`OUTLIER_WINDOW_M`. */
const OUTLIER_WINDOW_M = 3;
const CANT_OUTLIER_M = 0.008;
const SPACING_OUTLIER_M = 0.02;
const LATERAL_OUTLIER_M = 0.025;
const HEIGHT_OUTLIER_M = 0.02;
/** Above the fixed floors, an outlier stands this many robust sigmas out from the trace's own noise. */
const OUTLIER_SIGMAS = 4;
/**
 * Suspect stretches of one kind closer than this are one (m): one misread object (a sleeper
 * clip, a person) comes out as a few runs of rejected stations a few metres apart, and a list
 * row each was two rows to check for one thing. Interpolating the merged stretch bridges its
 * good stations in between too: a chord of under 10 m on a track.
 */
const MERGE_SUSPECTS_M = 10;
/** One-rail runs longer than this are suspect (the one rail may not be ours) (m). */
const ONE_RAIL_SUSPECT_M = 10;
/** A face-measured gauge this far from nominal is suspect (m). */
const GAUGE_SUSPECT_M = 0.03;

export interface ReviewSettings {
  /** Kept chainage range [from, to] in the TRACE's own order (m); null = everything. */
  trim: [number, number] | null;
  /** Stretches replaced by interpolation from their ends (trace chainage, m). */
  bridges: [number, number][];
  smoothingM: number;
  /** Save in the opposite direction (cant flips sign: left and right swap). */
  reversed: boolean;
  /**
   * Stretches to check the user has looked at and left as they are (trace chainage, m): the list
   * keeps them, ticked (`checkRows`). The review itself does not read them.
   */
  checked?: [number, number][];
}

export const DEFAULT_REVIEW: ReviewSettings = {
  trim: null,
  bridges: [],
  smoothingM: DEFAULT_SMOOTHING_M,
  reversed: false,
};

/** One station as the panel shows it, in trace order and trace chainage. */
export interface ProfilePoint {
  s: number;
  x: number;
  y: number;
  z: number;
  /** Measured (status 'both'), else interpolated. */
  cantM: number;
  /** Gauge between the inner head faces 14 mm under the top; on a sparse scan, head-centre spacing − 72 mm. */
  gaugeM: number;
  status: ReviewStatus;
  /** Raw left / right heads (for the 3D rails). */
  left: [number, number, number];
  right: [number, number, number];
}

export interface AlignmentVertex {
  x: number;
  y: number;
  z: number;
  cantM: number;
  gaugeM: number;
}

export interface ReviewResult {
  /** Every station after bridging + smoothing, trace order (the graph, the 3D preview). */
  profile: ProfilePoint[];
  /** The alignment as it will be saved: trimmed, resampled, in the SAVE direction. */
  vertices: AlignmentVertex[];
  lengthM: number;
  /** Trace length (m). */
  totalM: number;
  maxAbsCantM: number;
  gaugeMinM: number | null;
  gaugeMaxM: number | null;
  /** Median gauge over the kept stations measured on both rails: the figure the card shows. */
  gaugeMedianM: number | null;
  /** Share of the kept stations measured on both rails (0..1). */
  measuredShare: number;
  /** Kept vertices whose cant had to be clamped to ±0.25 m. */
  clampedCant: number;
  /** Stations dropped as outliers (their values interpolated). */
  rejected: number;
  /** Stretches to look at, in trace chainage. */
  suspects: SuspectStretch[];
  /** The track gauge set, else the nominal gauge nearest the kept stations' median measured gauge (m). */
  nominalGaugeM: number;
  /**
   * Share of the kept, both-rail stations whose gauge was measured on the
   * heads' faces, or lies between two such readings within `GAUGE_BETWEEN_M`
   * (0..1); the rest are estimated from head-centre spacing.
   */
  gaugeFacesShare: number;
  /**
   * Level crossings in the kept stretch (trace chainage, each run clipped to
   * the trim): the position measured from the flangeway grooves, cant and
   * gauge interpolated across. Not a suspect: shown for information.
   */
  crossings: { from: number; to: number }[];
}

export const chainages = (stations: readonly RailStation[]): number[] => {
  const s = [0];
  for (let i = 1; i < stations.length; i++) {
    const a = stations[i - 1].centre;
    const b = stations[i].centre;
    s.push(s[i - 1] + Math.hypot(b.x - a.x, b.y - a.y));
  }
  return s;
};

const spacingOf = (st: RailStation) => Math.hypot(st.right.x - st.left.x, st.right.y - st.left.y);
export const cantOfStation = (st: RailStation) => {
  const sp = spacingOf(st);
  return sp > 0 ? ((st.right.z - st.left.z) * CANT_BASE_M) / sp : st.right.z - st.left.z;
};

/** Fill NaNs by linear interpolation in s between known neighbours (held flat past the ends). */
function fillLinear(s: readonly number[], v: number[]): number[] {
  const known: number[] = [];
  v.forEach((x, i) => Number.isFinite(x) && known.push(i));
  if (known.length === 0) return v.map(() => 0);
  const out = v.slice();
  let k = 0;
  for (let i = 0; i < v.length; i++) {
    if (Number.isFinite(v[i])) continue;
    while (k + 1 < known.length && known[k + 1] < i) k++;
    const a = known[k];
    const b = known[k + 1];
    if (a > i) out[i] = v[a];
    else if (b == null) out[i] = v[a];
    else out[i] = v[a] + ((v[b] - v[a]) * (s[i] - s[a])) / (s[b] - s[a]);
  }
  return out;
}

/** Triangular moving average over ±window/2 of chainage (the window shrinks at the ends so they don't bend). */
function smooth(s: readonly number[], v: readonly number[], window: number): number[] {
  if (window <= 0 || v.length < 3) return v.slice();
  const half = window / 2;
  return v.map((_, i) => {
    const h = Math.min(half, s[i] - s[0], s[s.length - 1] - s[i]);
    if (h <= 0) return v[i];
    let w = 0;
    let acc = 0;
    for (let j = i; j >= 0 && s[i] - s[j] <= h; j--) {
      const k = 1 - (s[i] - s[j]) / (h + 1e-9);
      w += k;
      acc += k * v[j];
    }
    for (let j = i + 1; j < v.length && s[j] - s[i] <= h; j++) {
      const k = 1 - (s[j] - s[i]) / (h + 1e-9);
      w += k;
      acc += k * v[j];
    }
    return acc / w;
  });
}

/** Interpolation at increasing chainages: the cursor only moves forward (O(n) over a whole resample). */
const interpolator = (s: readonly number[]) => {
  let i = 1;
  return (at: number) => {
    if (at <= s[0]) return { i: 1, t: 0 };
    while (i < s.length - 1 && s[i] < at) i++;
    const t = Math.min(1, (at - s[i - 1]) / (s[i] - s[i - 1] || 1));
    return { i, t };
  };
};
const lerp = (v: readonly number[], { i, t }: { i: number; t: number }) =>
  v.length === 1 ? v[0] : v[i - 1] + (v[i] - v[i - 1]) * t;

const round = (v: number, step: number) => {
  const r = Math.round(v / step) * step;
  return r === 0 ? 0 : Number(r.toFixed(6));
};

/**
 * What a review computes that the trim and direction never change: statuses,
 * the outlier rejection, filled and smoothed positions, cant and gauge. About
 * 40 ms on 7 km, and the card, the 3D line and the map each review the same
 * trace, so dragging a trim handle re-ran it three times a pointer move: it is
 * kept per trace (`baseCache`) and only recomputed when the bridges or the
 * smoothing change.
 */
interface ReviewBase {
  s: number[];
  totalM: number;
  status: ReviewStatus[];
  rejectedIdx: number[];
  x: number[];
  y: number[];
  z: number[];
  cant: number[];
  gauge: number[];
  between: boolean[];
  profile: ProfilePoint[];
  /** `suspectStretches` per nominal gauge. */
  suspects: Map<number, SuspectStretch[]>;
}

const baseCache = new WeakMap<readonly RailStation[], { key: string; base: ReviewBase }>();

function reviewBase(stations: readonly RailStation[], settings: ReviewSettings): ReviewBase {
  const key = JSON.stringify([settings.bridges, settings.smoothingM]);
  const hit = baseCache.get(stations);
  if (hit && hit.key === key) return hit.base;
  const s = chainages(stations);
  const totalM = s.length ? s[s.length - 1] : 0;
  // Never the trace's own first or last station: a bridge is drawn between two measured ends, and one
  // reaching an end (or the whole trace) would otherwise hold the line flat there (or put it at 0, 0).
  const inBridge = (i: number) =>
    i > 0 &&
    i < stations.length - 1 &&
    settings.bridges.some(([a, b]) => s[i] >= Math.min(a, b) && s[i] <= Math.max(a, b));
  const status: ReviewStatus[] = stations.map((st, i) => (inBridge(i) ? 'bridged' : st.status));
  // A station rebuilt from a saved alignment is the reviewed line itself: never an outlier.
  const rejectedIdx = stations.every((st) => st.saved)
    ? []
    : rejectOutliers(stations, s, status).filter((i) => !stations[i].saved);
  for (const i of rejectedIdx) status[i] = 'rejected';
  // Positions: measured stations as found; gaps and bridged stretches drawn straight between their ends.
  const posKnown = (i: number) => status[i] !== 'gap' && status[i] !== 'bridged' && status[i] !== 'rejected';
  const pick = (f: (st: RailStation) => number, ok: (i: number) => boolean) =>
    stations.map((st, i) => (ok(i) ? f(st) : NaN));
  const both = (i: number) => status[i] === 'both';
  let x = fillLinear(
    s,
    pick((st) => st.centre.x, posKnown),
  );
  let y = fillLinear(
    s,
    pick((st) => st.centre.y, posKnown),
  );
  let z = fillLinear(
    s,
    pick((st) => st.centre.z, posKnown),
  );
  let cant = fillLinear(s, pick(cantOfStation, both));
  // Gauge: measured face to face where a station has it; between two such readings no more than
  // `GAUGE_BETWEEN_M` either side (the Fast precision samples it every 25 m), interpolated from them;
  // elsewhere estimated from the head spacing in the canted rail plane (±15 mm, like `gaugeOf`).
  const between = gaugeBetween(stations, s, both);
  let gauge = fillLinear(
    s,
    stations.map((st, i) =>
      !both(i)
        ? NaN
        : st.gaugeM ?? (between[i] ? NaN : Math.hypot(spacingOf(st), st.right.z - st.left.z) - RAIL_HEAD_WIDTH_M),
    ),
  );
  // Positions are smoothed over at most 5 m: a window of length h pulls a
  // curve of radius R in by about h²/(12R) (20 m on R 150 m: 22 mm, enough to
  // bias a clearance run). Cant and gauge take the full window.
  const posWindow = Math.min(settings.smoothingM, MAX_POSITION_SMOOTHING_M);
  const unsmoothed = [x, y, z, cant, gauge];
  x = smooth(s, x, posWindow);
  y = smooth(s, y, posWindow);
  z = smooth(s, z, posWindow);
  cant = smooth(s, cant, settings.smoothingM);
  gauge = smooth(s, gauge, settings.smoothingM);
  // A saved alignment was smoothed when it was saved: its stations keep their values (smoothing it
  // again on every edit would wear it down); only what is traced on to it is smoothed.
  const smoothed = [x, y, z, cant, gauge];
  stations.forEach((st, i) => {
    if (st.saved && status[i] === 'both') for (let k = 0; k < 5; k++) smoothed[k][i] = unsmoothed[k][i];
  });

  const profile: ProfilePoint[] = stations.map((st, i) => ({
    s: s[i],
    x: x[i],
    y: y[i],
    z: z[i],
    cantM: cant[i],
    gaugeM: gauge[i],
    status: status[i],
    left: [st.left.x, st.left.y, st.left.z],
    right: [st.right.x, st.right.y, st.right.z],
  }));
  const base: ReviewBase = {
    s,
    totalM,
    status,
    rejectedIdx,
    x,
    y,
    z,
    cant,
    gauge,
    between,
    profile,
    suspects: new Map(),
  };
  baseCache.set(stations, { key, base });
  return base;
}

export function reviewTrace(
  stations: readonly RailStation[],
  settings: ReviewSettings,
  /** The track gauge set (`trackGaugeM`): graded against it; null/unset = the nominal nearest the measured median. */
  nominalGaugeM: number | null = null,
): ReviewResult {
  const { s, totalM, status, rejectedIdx, x, y, z, cant, gauge, between, profile, suspects } = reviewBase(
    stations,
    settings,
  );
  const [from, to] = settings.trim
    ? [Math.max(0, Math.min(...settings.trim)), Math.min(totalM, Math.max(...settings.trim))]
    : [0, totalM];
  const lengthM = Math.max(0, to - from);
  const vertices: AlignmentVertex[] = [];
  if (stations.length >= 2 && lengthM > 0) {
    const n = Math.max(1, Math.round(lengthM / VERTEX_SPACING_M));
    const locate = interpolator(s);
    for (let k = 0; k <= n; k++) {
      const p = locate(from + (lengthM * k) / n);
      vertices.push({ x: lerp(x, p), y: lerp(y, p), z: lerp(z, p), cantM: lerp(cant, p), gaugeM: lerp(gauge, p) });
    }
  }
  if (settings.reversed) {
    vertices.reverse();
    for (const v of vertices) v.cantM = -v.cantM;
  }
  let clampedCant = 0;
  for (const v of vertices) {
    if (Math.abs(v.cantM) > MAX_ABS_CANT_M) {
      clampedCant++;
      v.cantM = Math.sign(v.cantM) * MAX_ABS_CANT_M;
    }
  }
  const kept = profile.filter((p) => p.s >= from - 1e-9 && p.s <= to + 1e-9);
  const keptBoth = kept.filter((p) => p.status === 'both');
  const gauges = keptBoth.map((p) => p.gaugeM);
  const sortedG = [...gauges].sort((a, b) => a - b);
  const nominal =
    nominalGaugeM ?? (sortedG.length ? nominalGauge(sortedG[Math.floor(sortedG.length / 2)]) : STANDARD_GAUGE_M);
  const keptIdx = stations.flatMap((_, i) => (s[i] >= from - 1e-9 && s[i] <= to + 1e-9 && status[i] === 'both' ? [i] : []));
  return {
    profile,
    vertices,
    lengthM,
    totalM,
    maxAbsCantM: vertices.reduce((m, v) => Math.max(m, Math.abs(v.cantM)), 0),
    gaugeMinM: sortedG.length ? sortedG[0] : null,
    gaugeMaxM: sortedG.length ? sortedG[sortedG.length - 1] : null,
    gaugeMedianM: sortedG.length ? sortedG[Math.floor(sortedG.length / 2)] : null,
    measuredShare: kept.length ? keptBoth.length / kept.length : 0,
    nominalGaugeM: nominal,
    rejected: rejectedIdx.length,
    suspects:
      suspects.get(nominal) ?? suspects.set(nominal, suspectStretches(stations, s, status, nominal)).get(nominal)!,
    crossings: runs(s, (i) => status[i] === 'crossing')
      .map(([a, b]) => ({ from: Math.max(a, from), to: Math.min(b, to) }))
      .filter((c) => c.to > c.from),
    gaugeFacesShare: keptIdx.length
      ? keptIdx.filter((i) => stations[i].gaugeM != null || between[i]).length / keptIdx.length
      : 0,
    clampedCant,
  };
}

/** A station without a gauge of its own is read between two that have one, no further than this either side (m). */
export const GAUGE_BETWEEN_M = 30;

/**
 * Per station: no gauge of its own, but a face-measured one within `GAUGE_BETWEEN_M` before and
 * after it (past the last reading at either end of the trace, within that distance of it).
 */
export function gaugeBetween(
  stations: readonly RailStation[],
  s: readonly number[],
  ok: (i: number) => boolean,
): boolean[] {
  const out = stations.map(() => false);
  let last = -Infinity;
  const prevAt = stations.map((st, i) => {
    const p = last;
    if (ok(i) && st.gaugeM != null) last = s[i];
    return p;
  });
  let next = Infinity;
  for (let i = stations.length - 1; i >= 0; i--) {
    if (ok(i) && stations[i].gaugeM == null) {
      const before = s[i] - prevAt[i] <= GAUGE_BETWEEN_M;
      const after = next - s[i] <= GAUGE_BETWEEN_M;
      out[i] = (before && after) || (before && next === Infinity) || (after && prevAt[i] === -Infinity);
    }
    if (ok(i) && stations[i].gaugeM != null) next = s[i];
  }
  return out;
}

/** Gauge class of a measured gauge against its nominal (standard gauge by default). */
export const gaugeClass = (gaugeM: number, nominalM: number = STANDARD_GAUGE_M): 'ok' | 'check' | 'off' => {
  const d = Math.abs(gaugeM - nominalM);
  return d <= GAUGE_OK_M ? 'ok' : d <= 2 * GAUGE_OK_M ? 'check' : 'off';
};

export interface RailAlignmentFeature {
  geometry: { type: 'LineString'; coordinates: [number, number, number][] };
  properties: {
    featureType: 'line';
    Name: string;
    alignment: {
      name: string;
      cantM: number[];
      gaugeM: number[];
      /** Made by "Alignment from rails": the tool may open it again (`editableRailFeature`). */
      source: 'railTrace';
      /** The point cloud it was traced on (continuing it reads that one), when known. */
      cloudLayerId?: string;
    };
  };
}

/** The LineString + properties saved for the reviewed vertices (cant to 0.1 mm, gauge to 1 mm). */
export function buildRailAlignmentFeature(
  vertices: readonly AlignmentVertex[],
  name: string,
  cloudLayerId?: string | null,
): RailAlignmentFeature {
  const trimmed = name.trim();
  return {
    geometry: { type: 'LineString', coordinates: vertices.map((v) => [v.x, v.y, v.z]) },
    properties: {
      featureType: 'line',
      Name: trimmed,
      alignment: {
        name: trimmed,
        cantM: vertices.map((v) => round(v.cantM, 1e-4)),
        gaugeM: vertices.map((v) => round(v.gaugeM, 1e-3)),
        source: 'railTrace',
        ...(cloudLayerId ? { cloudLayerId } : {}),
      },
    },
  };
}

/** Median of a few numbers. */
const med = (a: number[]) => {
  const b = [...a].sort((p, q) => p - q);
  const m = b.length >> 1;
  return b.length % 2 ? b[m] : (b[m - 1] + b[m]) / 2;
};

/** Least-squares quadratic w(u) through the points, evaluated at u = 0 (the station itself is left out by the caller). */
function quadAt0(us: number[], ws: number[]): number {
  if (us.length < 3) return ws.length ? med(ws) : 0;
  // Normal equations for w = a + b·u + c·u².
  let s0 = 0,
    s1 = 0,
    s2 = 0,
    s3 = 0,
    s4 = 0,
    t0 = 0,
    t1 = 0,
    t2 = 0;
  for (let k = 0; k < us.length; k++) {
    const u = us[k];
    const u2 = u * u;
    s0 += 1;
    s1 += u;
    s2 += u2;
    s3 += u2 * u;
    s4 += u2 * u2;
    t0 += ws[k];
    t1 += ws[k] * u;
    t2 += ws[k] * u2;
  }
  const det = s0 * (s2 * s4 - s3 * s3) - s1 * (s1 * s4 - s3 * s2) + s2 * (s1 * s3 - s2 * s2);
  if (Math.abs(det) < 1e-12) return med(ws);
  return (t0 * (s2 * s4 - s3 * s3) - s1 * (t1 * s4 - s3 * t2) + s2 * (t1 * s3 - s2 * t2)) / det;
}

/**
 * Measured stations that disagree with their neighbours: something on a rail
 * head, a stray cluster taken for a head, a single bad slice. Compared within
 * ±3 m (neighbours of the same kind, the station itself left out):
 *   - cant more than 8 mm from the neighbours' median (real cant ramps are
 *     a few mm per metre at most),
 *   - head spacing more than 20 mm from theirs,
 *   - the centre more than 25 mm across, or 20 mm in height, from a
 *     quadratic through the neighbours (a curve stays quadratic over 6 m).
 * Those are the floors for a clean scan; on a noisier one each limit rises to
 * 4 robust sigmas of that measure over the whole trace, so a sparse scan's
 * ordinary scatter is not flagged station after station.
 * Returns the indices; their values are then interpolated like a gap's.
 */
export function rejectOutliers(
  stations: readonly RailStation[],
  s: readonly number[],
  status: readonly ReviewStatus[],
): number[] {
  const measured = (i: number) => status[i] === 'both' || status[i] === 'left' || status[i] === 'right';
  // Each station's residual against its neighbours, per measure (NaN = not tested).
  const n = stations.length;
  const res = {
    cant: new Float64Array(n).fill(NaN),
    spacing: new Float64Array(n).fill(NaN),
    lateral: new Float64Array(n).fill(NaN),
    height: new Float64Array(n).fill(NaN),
  };
  for (let i = 0; i < n; i++) {
    if (!measured(i)) continue;
    const nb: number[] = [];
    for (let j = i - 1; j >= 0 && s[i] - s[j] <= OUTLIER_WINDOW_M; j--) if (measured(j)) nb.push(j);
    for (let j = i + 1; j < n && s[j] - s[i] <= OUTLIER_WINDOW_M; j++) if (measured(j)) nb.push(j);
    if (nb.length < 3) continue;
    const st = stations[i];
    if (status[i] === 'both') {
      const bothNb = nb.filter((j) => status[j] === 'both');
      if (bothNb.length >= 3) {
        res.cant[i] = cantOfStation(st) - med(bothNb.map((j) => cantOfStation(stations[j])));
        res.spacing[i] = spacingOf(st) - med(bothNb.map((j) => spacingOf(stations[j])));
      }
    }
    // Local frame: u along the neighbours' chord, w across it.
    const a = stations[nb.reduce((p, j) => (s[j] < s[p] ? j : p), nb[0])].centre;
    const b = stations[nb.reduce((p, j) => (s[j] > s[p] ? j : p), nb[0])].centre;
    const L = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    const dx = (b.x - a.x) / L;
    const dy = (b.y - a.y) / L;
    const us = nb.map((j) => (stations[j].centre.x - st.centre.x) * dx + (stations[j].centre.y - st.centre.y) * dy);
    const ws = nb.map((j) => -(stations[j].centre.x - st.centre.x) * dy + (stations[j].centre.y - st.centre.y) * dx);
    const zs = nb.map((j) => stations[j].centre.z - st.centre.z);
    res.lateral[i] = quadAt0(us, ws);
    res.height[i] = quadAt0(us, zs);
  }
  // A sparse or low-grade scan is noisier everywhere: an outlier is what stands
  // out from THIS trace's own noise (4 × its robust sigma), never less than the
  // fixed floor that a clean scan is held to.
  const limit = (r: Float64Array, floor: number) => {
    const vals = Array.from(r).filter(Number.isFinite);
    if (vals.length < 10) return floor;
    const m = med(vals);
    const mad = med(vals.map((v) => Math.abs(v - m)));
    return Math.max(floor, OUTLIER_SIGMAS * 1.4826 * mad);
  };
  const lim = {
    cant: limit(res.cant, CANT_OUTLIER_M),
    spacing: limit(res.spacing, SPACING_OUTLIER_M),
    lateral: limit(res.lateral, LATERAL_OUTLIER_M),
    height: limit(res.height, HEIGHT_OUTLIER_M),
  };
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    if (
      Math.abs(res.cant[i]) > lim.cant ||
      Math.abs(res.spacing[i]) > lim.spacing ||
      Math.abs(res.lateral[i]) > lim.lateral ||
      Math.abs(res.height[i]) > lim.height
    ) {
      out.push(i);
    }
  }
  return out;
}

/** Runs of stations matching `pred`, as chainage stretches (half a step padded each side). */
function runs(s: readonly number[], pred: (i: number) => boolean): [number, number][] {
  const out: [number, number][] = [];
  let start = -1;
  for (let i = 0; i <= s.length; i++) {
    if (i < s.length && pred(i)) {
      if (start < 0) start = i;
    } else if (start >= 0) {
      out.push([(s[Math.max(0, start - 1)] + s[start]) / 2, (s[i - 1] + s[Math.min(s.length - 1, i)]) / 2]);
      start = -1;
    }
  }
  return out;
}

/**
 * What the user should look at before saving: two or more outliers in a row
 * (a stretch the follower mis-read, not one bad slice), more than 10 m on one
 * rail, and a face-measured gauge more than 30 mm off nominal (a misread
 * head — or a real track defect worth knowing about).
 */
export function suspectStretches(
  stations: readonly RailStation[],
  s: readonly number[],
  status: readonly ReviewStatus[],
  nominalM: number,
): SuspectStretch[] {
  const out: SuspectStretch[] = [];
  for (const [from, to] of runs(s, (i) => status[i] === 'rejected')) {
    const n = status.filter((st, i) => st === 'rejected' && s[i] >= from && s[i] <= to).length;
    if (n >= 2) out.push({ from, to, reason: 'outliers' });
  }
  for (const [from, to] of runs(s, (i) => status[i] === 'left' || status[i] === 'right')) {
    if (to - from > ONE_RAIL_SUSPECT_M) out.push({ from, to, reason: 'oneRail' });
  }
  for (const [from, to] of runs(
    s,
    (i) =>
      status[i] === 'both' && stations[i].gaugeM != null && Math.abs(stations[i].gaugeM! - nominalM) > GAUGE_SUSPECT_M,
  )) {
    let devM = 0;
    for (let i = 0; i < stations.length; i++) {
      if (s[i] < from || s[i] > to || status[i] !== 'both' || stations[i].gaugeM == null) continue;
      const d = stations[i].gaugeM! - nominalM;
      if (Math.abs(d) > Math.abs(devM)) devM = d;
    }
    out.push({ from, to, reason: 'gauge', devM });
  }
  // One misread object reads as one stretch: same-kind stretches under MERGE_SUSPECTS_M apart are merged.
  const merged: SuspectStretch[] = [];
  for (const x of out.sort((p, q) => p.from - q.from)) {
    const prev = [...merged].reverse().find((m) => m.reason === x.reason);
    if (prev && x.from - prev.to < MERGE_SUSPECTS_M) {
      prev.to = Math.max(prev.to, x.to);
      if (x.devM !== undefined && Math.abs(x.devM) > Math.abs(prev.devM ?? 0)) prev.devM = x.devM;
    }
    else merged.push({ ...x });
  }
  return merged;
}
